import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AttachMoneyOutlined,
  CalendarTodayOutlined,
  CloseRounded,
  DoneRounded,
  GroupOutlined,
  LocalDiningOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import api from '../../utils/Api';
import {
  WEEK_DAYS,
  getApiErrorMessage,
  getClassDisplayName,
  getSchoolId,
  getStoredTeacher,
  teacherPortalGridItemSx,
  teacherPortalStatCardSx,
  teacherPortalTableCardSx,
} from './teacherPortalUtils';

const extractWeekNumber = (week) => String(week || '').replace('Week ', '');

export default function TeacherFeedingFeeScreen() {
  const theme = useTheme();
  const teacher = getStoredTeacher();
  const schoolId = getSchoolId(teacher);
  const classes = Array.isArray(teacher.assignedClasses) ? teacher.assignedClasses : [];
  const [terms, setTerms] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(classes[0]?._id || '');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');

  const selectedTerm = useMemo(
    () => terms.find((term) => term._id === selectedTermId),
    [selectedTermId, terms]
  );

  const loadTerms = useCallback(async () => {
    if (!schoolId) return;
    try {
      const res = await api.get('/term/all', { params: { school: schoolId } });
      const nextTerms = res.data?.allTerms || res.data?.terms || [];
      setTerms(nextTerms);
      setSelectedTermId((prev) => prev || nextTerms[0]?._id || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load terms.'));
    }
  }, [schoolId]);

  const loadWeeks = useCallback(async () => {
    if (!schoolId || !selectedTerm) return;
    try {
      const res = await api.get('/term/weeks', {
        params: {
          school: schoolId,
          academicYear: selectedTerm.academicYear,
          term: selectedTerm.term,
          termId: selectedTerm._id,
        },
      });
      const fetchedWeeks = res.data?.weeks || [];
      const labels = fetchedWeeks
        .map((week) => week.label)
        .filter(Boolean)
        .sort((a, b) => Number(extractWeekNumber(a)) - Number(extractWeekNumber(b)));
      const currentWeekLabel = fetchedWeeks.find((week) => week?.isCurrent && week?.label)?.label;

      setWeeks(labels);
      setSelectedWeek(currentWeekLabel || labels[0] || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load weeks.'));
    }
  }, [schoolId, selectedTerm]);

  const loadRecords = useCallback(async () => {
    if (!selectedClassId || !selectedTermId || !selectedWeek) return;
    setLoading(true);
    setError('');
    try {
      const week = extractWeekNumber(selectedWeek);
      const [calcRes, summaryRes] = await Promise.allSettled([
        api.post('/feeding-fees/calculate', {
          classId: selectedClassId,
          termId: selectedTermId,
          week,
          school: schoolId,
        }),
        api.get('/feeding-fees/summary', {
          params: { classId: selectedClassId, termId: selectedTermId, week, school: schoolId },
        }),
      ]);

      if (calcRes.status === 'rejected') throw calcRes.reason;

      const nextRecords = (calcRes.value.data?.breakdown || []).sort((a, b) =>
        (a.studentName || '').localeCompare(b.studentName || '')
      );
      setRecords(nextRecords);
      setSummary(summaryRes.status === 'fulfilled' ? summaryRes.value.data : calcRes.value.data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load feeding fee records.'));
      setRecords([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClassId, selectedTermId, selectedWeek]);

  useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  useEffect(() => {
    setSelectedWeek('');
    loadWeeks();
  }, [loadWeeks]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const markFeeding = async (studentId, day, fed) => {
    if (!selectedTermId || !selectedClassId || !selectedWeek) return;
    const markId = `${studentId}-${day}`;
    setSavingId(markId);
    setError('');
    try {
      await api.post('/feeding-fees/mark-bulk', {
        termId: selectedTermId,
        classId: selectedClassId,
        week: extractWeekNumber(selectedWeek),
        marks: [{ student: studentId, day, fed }],
      });
      setRecords((prev) =>
        prev.map((record) =>
          record.studentId === studentId
            ? {
                ...record,
                days: {
                  ...(record.days || {}),
                  [day]: fed ? 'present' : 'notmarked',
                },
              }
            : record
        )
      );
      loadRecords();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to save feeding fee mark.'));
    } finally {
      setSavingId('');
    }
  };

  const studentCount = summary?.studentCount ?? records.length;
  const totalAmount =
    Number(summary?.totalAmount || 0) ||
    records.reduce((total, record) => total + Number(record.total || 0), 0);
  const totalMeals = records.reduce(
    (total, record) =>
      total + WEEK_DAYS.filter((day) => record.days?.[day.key] === 'present').length,
    0
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Feeding Fee
          </Typography>
          <Typography color="text.secondary">Mark daily feeding payments.</Typography>
        </Box>
        <Button
          startIcon={<RefreshOutlined />}
          variant="outlined"
          onClick={loadRecords}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 34, md: 42 }, height: { xs: 34, md: 42 } }}>
              <CalendarTodayOutlined fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Fee Setup & Filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select the class, term, and week before marking payments.
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select label="Class" value={selectedClassId} onChange={(event) => setSelectedClassId(event.target.value)}>
                  {classes.map((cls) => (
                    <MenuItem key={cls._id} value={cls._id}>
                      {getClassDisplayName(cls)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Term</InputLabel>
                <Select label="Term" value={selectedTermId} onChange={(event) => setSelectedTermId(event.target.value)}>
                  {terms.map((term) => (
                    <MenuItem key={term._id} value={term._id}>
                      {term.term} • {term.academicYear}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Week</InputLabel>
                <Select label="Week" value={selectedWeek} onChange={(event) => setSelectedWeek(event.target.value)}>
                  {weeks.map((week) => (
                    <MenuItem key={week} value={week}>
                      {week}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {[
          { label: 'Total Collected', value: `${summary?.currency || 'GHS'} ${totalAmount.toFixed(2)}`, icon: AttachMoneyOutlined, color: 'primary.main' },
          { label: 'Students', value: studentCount, icon: GroupOutlined, color: 'info.main' },
          { label: 'Meals Marked', value: totalMeals, icon: LocalDiningOutlined, color: 'success.main' },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <Grid item xs={12} md={4} key={stat.label} sx={teacherPortalGridItemSx}>
              <Card sx={teacherPortalStatCardSx(theme)}>
                <CardContent sx={{ width: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: stat.color }}>
                      <Icon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {stat.label}
                      </Typography>
                      <Typography variant="h5" fontWeight={900}>
                        {stat.value}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Card sx={teacherPortalTableCardSx(theme)}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'success.main', width: { xs: 34, md: 40 }, height: { xs: 34, md: 40 } }}>
                <LocalDiningOutlined fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Feeding Fee Records ({records.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedWeek || 'No week selected'}
                </Typography>
              </Box>
            </Stack>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 900 }}>Student</TableCell>
                    {WEEK_DAYS.map((day) => (
                      <TableCell key={day.key} align="center" sx={{ fontWeight: 900 }}>
                        {day.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 900 }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.studentId}>
                      <TableCell sx={{ fontWeight: 700 }}>{record.studentName}</TableCell>
                      {WEEK_DAYS.map((day) => {
                        const paid = record.days?.[day.key] === 'present';
                        const busy = savingId === `${record.studentId}-${day.key}`;
                        return (
                          <TableCell key={day.key} align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Button
                                size="small"
                                variant={paid ? 'contained' : 'outlined'}
                                color="success"
                                disabled={busy || record.isExemptFromFeedingFee}
                                onClick={() => markFeeding(record.studentId, day.key, true)}
                                sx={{ minWidth: 36 }}
                              >
                                {busy ? <CircularProgress size={14} /> : <DoneRounded fontSize="small" />}
                              </Button>
                              <Button
                                size="small"
                                variant={!paid ? 'contained' : 'outlined'}
                                color="inherit"
                                disabled={busy || record.isExemptFromFeedingFee}
                                onClick={() => markFeeding(record.studentId, day.key, false)}
                                sx={{ minWidth: 36 }}
                              >
                                <CloseRounded fontSize="small" />
                              </Button>
                            </Stack>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">{Number(record.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {records.length === 0 && (
                <Typography color="text.secondary" sx={{ p: 3 }}>
                  No feeding fee records found.
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
